import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Tag,
  Input,
  Dropdown,
  Progress,
  Empty,
  Tooltip,
  Modal,
  Select,
  Typography,
  Space,
  Breadcrumb,
} from "antd";
import {
  FiPlus,
  FiSearch,
  FiPlay,
  FiPause,
  FiSquare,
  FiTrash2,
  FiEye,
  FiMoreVertical,
  FiRefreshCw,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiActivity,
  FiHome,
  FiFileText,
  FiArrowLeft,
} from "react-icons/fi";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  SuccessNotification,
  ErrorNotification,
} from "@components";
import {
  getBlogCampaignsPaginated,
  getBlogCampaignDashboardStats,
  startBlogCampaign,
  pauseBlogCampaign,
  resumeBlogCampaign,
  stopBlogCampaign,
  deleteBlogCampaign,
} from "@api/blog/blogCampaign";

const { Title, Text } = Typography;
const { Search } = Input;

const statusColors = {
  draft: "default",
  scheduled: "blue",
  generating: "processing",
  paused: "warning",
  completed: "success",
  stopped: "default",
  failed: "error",
};

const statusIcons = {
  draft: <FiClock />,
  scheduled: <FiClock />,
  generating: <FiActivity />,
  paused: <FiPause />,
  completed: <FiCheckCircle />,
  stopped: <FiSquare />,
  failed: <FiXCircle />,
};

const campaignTypeLabels = {
  topicBased: "Topic Based",
  keywordBased: "Keyword Based",
  seriesBased: "Blog Series",
};

const BlogCampaignsList = ({ history }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [stats, setStats] = useState({
    total: 0,
    generating: 0,
    completed: 0,
    failed: 0,
    totalBlogsGenerated: 0,
  });
  const [actionLoading, setActionLoading] = useState({});

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignsRes, statsRes] = await Promise.all([
        getBlogCampaignsPaginated(
          pagination.current,
          pagination.pageSize,
          statusFilter,
          searchText
        ),
        getBlogCampaignDashboardStats(),
      ]);

      setCampaigns(campaignsRes.data.campaigns);
      setPagination((prev) => ({
        ...prev,
        total: campaignsRes.data.pagination.total,
      }));
      setStats(statsRes.data.stats || statsRes.data);
    } catch (error) {
      console.error("Error fetching blog campaigns:", error);
      ErrorNotification("Failed to fetch blog campaigns");
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, statusFilter, searchText]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Polling for generating campaigns
  useEffect(() => {
    const hasGenerating = campaigns.some((c) => c.status === "generating");
    if (!hasGenerating) return;

    const interval = setInterval(fetchCampaigns, 10000);
    return () => clearInterval(interval);
  }, [campaigns, fetchCampaigns]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pag) => {
    setPagination({
      current: pag.current,
      pageSize: pag.pageSize,
      total: pagination.total,
    });
  };

  const handleAction = async (action, campaignId, campaignName) => {
    setActionLoading((prev) => ({ ...prev, [campaignId]: action }));

    try {
      switch (action) {
        case "start":
          await startBlogCampaign(campaignId);
          SuccessNotification(`Blog campaign "${campaignName}" started`);
          break;
        case "pause":
          await pauseBlogCampaign(campaignId);
          SuccessNotification(`Blog campaign "${campaignName}" paused`);
          break;
        case "resume":
          await resumeBlogCampaign(campaignId);
          SuccessNotification(`Blog campaign "${campaignName}" resumed`);
          break;
        case "stop":
          Modal.confirm({
            title: "Stop Campaign",
            content: `Are you sure you want to stop "${campaignName}"? This action cannot be undone.`,
            okText: "Stop",
            okType: "danger",
            onOk: async () => {
              await stopBlogCampaign(campaignId);
              SuccessNotification(`Blog campaign "${campaignName}" stopped`);
              fetchCampaigns();
            },
          });
          break;
        case "delete":
          Modal.confirm({
            title: "Delete Campaign",
            content: `Are you sure you want to delete "${campaignName}"?`,
            okText: "Delete",
            okType: "danger",
            onOk: async () => {
              await deleteBlogCampaign(campaignId);
              SuccessNotification(`Blog campaign "${campaignName}" deleted`);
              fetchCampaigns();
            },
          });
          break;
        default:
          break;
      }
      fetchCampaigns();
    } catch (error) {
      ErrorNotification(error.response?.data?.message || `Failed to ${action} campaign`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [campaignId]: null }));
    }
  };

  const getActionMenu = (campaign) => {
    const items = [];

    if (["draft", "scheduled"].includes(campaign.status)) {
      items.push({
        key: "start",
        icon: <FiPlay />,
        label: "Start",
        onClick: () => handleAction("start", campaign._id, campaign.name),
      });
    }

    if (campaign.status === "generating") {
      items.push({
        key: "pause",
        icon: <FiPause />,
        label: "Pause",
        onClick: () => handleAction("pause", campaign._id, campaign.name),
      });
    }

    if (campaign.status === "paused") {
      items.push({
        key: "resume",
        icon: <FiRefreshCw />,
        label: "Resume",
        onClick: () => handleAction("resume", campaign._id, campaign.name),
      });
    }

    if (["generating", "paused"].includes(campaign.status)) {
      items.push({
        key: "stop",
        icon: <FiSquare />,
        label: "Stop",
        danger: true,
        onClick: () => handleAction("stop", campaign._id, campaign.name),
      });
    }

    items.push({
      key: "view",
      icon: <FiEye />,
      label: "View Progress",
      onClick: () => history.push(`/super-admin/blog-campaign/${campaign._id}/progress`),
    });

    if (!["generating"].includes(campaign.status)) {
      items.push({
        key: "delete",
        icon: <FiTrash2 />,
        label: "Delete",
        danger: true,
        onClick: () => handleAction("delete", campaign._id, campaign.name),
      });
    }

    return { items };
  };

  const columns = [
    {
      title: "Campaign",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <Text strong style={{ fontSize: 14 }}>{name}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {campaignTypeLabels[record.campaignType]} • {record.topicConfig?.mainTopic || "No topic"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={statusColors[status]} icon={statusIcons[status]}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Progress",
      key: "progress",
      width: 180,
      render: (_, record) => {
        const { statistics } = record;
        const completed = statistics?.completedBlogs || 0;
        const total = statistics?.totalBlogs || record.generationConfig?.blogCount || 0;
        const failed = statistics?.failedBlogs || 0;
        const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

        return (
          <div>
            <Progress
              percent={percent}
              size="small"
              status={record.status === "failed" ? "exception" : undefined}
              strokeColor={record.status === "generating" ? "#1890ff" : undefined}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {completed}/{total} done
              {failed > 0 && <span style={{ color: "#ff4d4f" }}> • {failed} failed</span>}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Blogs",
      key: "blogCount",
      width: 90,
      align: "center",
      render: (_, record) => record.generationConfig?.blogCount || 0,
    },
    {
      title: "Avg SEO",
      key: "seoScore",
      width: 80,
      align: "center",
      render: (_, record) => {
        const score = record.statistics?.averageSeoScore || 0;
        return (
          <Tag color={score >= 80 ? "success" : score >= 60 ? "warning" : "default"}>
            {score}%
          </Tag>
        );
      },
    },
    {
      title: "Schedule",
      key: "schedule",
      width: 140,
      render: (_, record) => (
        <div>
          {record.scheduledFor ? (
            <Tooltip title={moment(record.scheduledFor).format("LLLL")}>
              <Text style={{ fontSize: 12 }}>
                <FiClock style={{ marginRight: 4, verticalAlign: "middle" }} />
                {moment(record.scheduledFor).format("MMM D, h:mm A")}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>Immediate</Text>
          )}
        </div>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {moment(date).format("MMM D, YYYY")}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <Dropdown
          menu={getActionMenu(record)}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<FiMoreVertical />}
            loading={!!actionLoading[record._id]}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px", background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>
            <Link to="/super-admin/dashboard"><FiHome style={{ marginRight: 4 }} />Dashboard</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/super-admin/blogs">Blogs</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Campaigns</Breadcrumb.Item>
        </Breadcrumb>

        <Row justify="space-between" align="middle">
          <Col>
            <Space align="center">
              <Button
                type="text"
                icon={<FiArrowLeft size={18} />}
                onClick={() => history.push("/super-admin/blogs")}
                style={{ marginRight: 8 }}
              />
              <FiFileText size={24} style={{ color: "#1890ff" }} />
              <div>
                <Title level={3} style={{ margin: 0 }}>Blog Campaigns</Title>
                <Text type="secondary">Automate bulk blog generation with AI</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<FiPlus />}
              size="large"
              onClick={() => history.push("/super-admin/blog-campaign/create")}
            >
              New Campaign
            </Button>
          </Col>
        </Row>
      </div>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #1890FF 0%, #096DD9 100%)",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: 600, display: "block", color: "#fff" }}>{stats.total}</Text>
              <Text style={{ color: "rgba(255, 255, 255, 0.85)" }}>Total Campaigns</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #722ED1 0%, #531DAB 100%)",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: 600, display: "block", color: "#fff" }}>{stats.totalBlogsGenerated || 0}</Text>
              <Text style={{ color: "rgba(255, 255, 255, 0.85)" }}>Blogs Generated</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #52C41A 0%, #389E0D 100%)",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: 600, display: "block", color: "#fff" }}>{stats.completed}</Text>
              <Text style={{ color: "rgba(255, 255, 255, 0.85)" }}>Completed</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #FAAD14 0%, #D48806 100%)",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: 600, display: "block", color: "#fff" }}>{stats.generating}</Text>
              <Text style={{ color: "rgba(255, 255, 255, 0.85)" }}>Generating</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        bordered={false}
        style={{
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
        }}
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10} md={8}>
            <Search
              placeholder="Search campaigns..."
              allowClear
              onSearch={handleSearch}
              prefix={<FiSearch style={{ color: "#bfbfbf" }} />}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by status"
              allowClear
              onChange={handleStatusFilter}
              value={statusFilter || undefined}
              options={[
                { value: "", label: "All Status" },
                { value: "draft", label: "Draft" },
                { value: "scheduled", label: "Scheduled" },
                { value: "generating", label: "Generating" },
                { value: "paused", label: "Paused" },
                { value: "completed", label: "Completed" },
                { value: "stopped", label: "Stopped" },
                { value: "failed", label: "Failed" },
              ]}
            />
          </Col>
          <Col>
            <Button icon={<FiRefreshCw />} onClick={fetchCampaigns}>
              Refresh
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `${total} campaigns`,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No blog campaigns yet"
                style={{ padding: "40px 0" }}
              >
                <Button
                  type="primary"
                  icon={<FiPlus />}
                  onClick={() => history.push("/super-admin/blog-campaign/create")}
                >
                  Create Your First Blog Campaign
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default BlogCampaignsList;
